/* Purchases/Home/PurchasesPageContainer.js */
import React, { ComponentType } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';
import { Link } from '~/lib/react-router-dom';

import { Box, Container, Tab, Tabs } from '@mui/material';

import CartPaneContainer from '~/components/Cart/CartPaneContainer';
import PageHeader from '~/components/Common/Elements/PageHeader';
import SmsVerifyWarning from '~/components/Common/SmsVerifyWarning';

import {
  ROLE_ADMIN_USER,
  ROLE_CRUISE_COORDINATOR,
  ROLE_EMPLOYEE_USER,
  ROLE_EXPERIENCES_COORDINATOR,
  ROLE_ICS_STORE_TEAM,
  ROLE_TOUR_COORDINATOR,
} from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';
import useQueryParams from '~/hooks/useQuery';

import canShowTab from '~/utils/canShowTab';

import AddonsPage from './AddonsPageContainer';
import OrdersPage from './AllOrders/OrdersPageContainer';
import BedbankPage from './BedbankPageContainer';
import BundleAndSavePage from './BundleAndSavePageContainer';
import CarHirePage from './CarHire/CarHirePurchasePage';
import CruisesPage from './Cruises/CruisesPurchasePage';
import CustomOffersPage from './CustomOffers';
import FlightsPage from './FlightsPageContainer';
import GiftCardsPage from './GiftCardsPageContainer';
import InsurancesPage from './InsurancesPageContainer';
import { LuxPlusPageContainer } from './LuxPlus';
import MerchandiseOrdersPage from './MerchandiseOrders/MerchandiseOrdersPageContainer';
import PackagesPage from './PackagesPageContainer';
import ToursPage from './ToursPageContainer';

const TAB_ALL_ORDERS = 'allOrders';
type Tab = {
  label: string;
  pageTitle: string;
  component: ComponentType;
  showTab?: boolean;
};

function defaultTabByRole(user) {
  if (user?.roles?.includes(ROLE_ADMIN_USER) || user?.roles?.includes(ROLE_EMPLOYEE_USER)) {
    return TAB_ALL_ORDERS;
  }

  if (user?.roles?.includes(ROLE_EXPERIENCES_COORDINATOR)) {
    return 'packages';
  }
  if (user?.roles?.includes(ROLE_TOUR_COORDINATOR)) {
    return 'tours';
  }

  if (user?.roles?.includes(ROLE_CRUISE_COORDINATOR)) {
    return 'cruises';
  }

  return TAB_ALL_ORDERS;
}

export default function PurchasesPageContainer() {
  const { user } = useCurrentUser();
  const { section = defaultTabByRole(user) } = useParams<{ section?: Tabs }>();
  const query = useQueryParams();
  const customerId = query.get('customer_id');

  const TABS_MAP = {
    [TAB_ALL_ORDERS]: {
      label: 'All Orders',
      pageTitle: 'Latest orders - All Types',
      component: OrdersPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    packages: {
      label: 'Packages',
      pageTitle: 'Latest orders',
      component: PackagesPage,
      showTab: canShowTab(user, [
        ROLE_ADMIN_USER,
        ROLE_EMPLOYEE_USER,
        ROLE_EXPERIENCES_COORDINATOR,
        ROLE_ICS_STORE_TEAM,
      ]),
    } as Tab,
    giftCards: {
      label: 'Gift Cards',
      pageTitle: 'Latest gift cards',
      component: GiftCardsPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    experiences: {
      label: 'Experiences',
      pageTitle: 'Latest experiences',
      component: AddonsPage,
      showTab: canShowTab(user, [
        ROLE_ADMIN_USER,
        ROLE_EMPLOYEE_USER,
        ROLE_EXPERIENCES_COORDINATOR,
        ROLE_ICS_STORE_TEAM,
      ]),
    } as Tab,
    flights: {
      label: 'Flights',
      pageTitle: 'Latest flights',
      component: FlightsPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    bedbanks: {
      label: 'Bedbanks',
      pageTitle: 'Bedbank orders',
      component: BedbankPage,
      showTab: canShowTab(user, [
        ROLE_ADMIN_USER,
        ROLE_EMPLOYEE_USER,
        ROLE_EXPERIENCES_COORDINATOR,
        ROLE_ICS_STORE_TEAM,
      ]),
    } as Tab,
    tours: {
      label: 'Tours',
      pageTitle: 'Latest tours',
      component: ToursPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_TOUR_COORDINATOR, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    packagesWithInsurance: {
      label: 'Insurance',
      pageTitle: 'Latest insurance',
      component: InsurancesPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    carts: {
      label: 'Abandoned Carts',
      pageTitle: 'Abandoned Carts',
      component: CartPaneContainer,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]),
    } as Tab,
    cruises: {
      label: 'Cruises',
      pageTitle: 'Latest cruises',
      component: CruisesPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM, ROLE_CRUISE_COORDINATOR]),
    } as Tab,
    carHire: {
      label: 'Car Hire',
      pageTitle: 'Latest bookings',
      component: CarHirePage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    shopOrders: {
      label: 'SHOP',
      pageTitle: 'Latest shop orders',
      component: MerchandiseOrdersPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    bundle: {
      label: 'Bundle and Save',
      pageTitle: 'Bundle and Save',
      component: BundleAndSavePage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    customOffers: {
      label: 'Custom Offers',
      pageTitle: 'Custom Offers',
      component: CustomOffersPage,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
    luxPlus: {
      label: 'Lux Plus',
      pageTitle: 'Lux Plus',
      component: LuxPlusPageContainer,
      showTab: canShowTab(user, [ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]),
    } as Tab,
  } as const;

  type Tabs = keyof typeof TABS_MAP;

  const tabList = Object.keys(TABS_MAP);
  const currentTab: Tab = TABS_MAP[section];

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{`Orders / ${currentTab.label}`}</title>
      </Helmet>

      <PageHeader title={currentTab.pageTitle} />

      <SmsVerifyWarning />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={section} id="orders" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          {tabList
            .filter((tabKey) => {
              const tab: Tab = TABS_MAP[tabKey];

              if (tab.showTab !== undefined) {
                return tab.showTab;
              }

              return true;
            })
            .map((tabKey) => {
              const tab: Tab = TABS_MAP[tabKey];

              return (
                <Tab
                  key={tabKey}
                  label={tab.label}
                  value={tabKey}
                  to={`/purchases/section/${tabKey}${customerId ? `?customer_id=${customerId}` : ''}`}
                  component={Link}
                />
              );
            })}
        </Tabs>
      </Box>

      <Box pt={4}>
        <Box component={currentTab.component} />
      </Box>
    </Container>
  );
}
